import React, { useState } from 'react';
import {
  AgFinancingFile,
  AgPhase3FileMetadata,
  AgPledgeCertificateCompanyRegistered,
  PledgeLevel
} from '@indigo-ag/schema';
import {
  Button,
  DatePicker,
  Input,
  palettes,
  Select,
  SelectChangeEvent,
  useFlashMessage
} from '@indigo-ag/component-module';
import { MaterialUiPickersDate } from 'material-ui-pickers';
import { ApolloError, useMutation } from '@apollo/client';

import i18next from 'core/i18n';
import { createStylesHook, GridContainer, pledgeLevelOptions, usePrompt, utils } from 'shared';
import { PledgeCertificateRegisteredCompany } from './PledgeCertificateRegisteredCompany/PledgeCertificateRegisteredCompany';
import { updateAgFinancingFile } from 'features/brazil-grower/queries';
import { PledgeCertificateDocumentSummary } from 'features/brazil-grower/components/shared/PledgeCertificateDocumentSummary/PledgeCertificateDocumentSummary';

const useStyles = createStylesHook({
  card: {
    border: `1px solid ${palettes.brand.gray[300]}`,
    borderRadius: 8,
    padding: 24,
    marginBottom: 16,
    '& .label': {
      color: palettes.brand.gray[700],
      marginBottom: 8
    }
  }
});

interface PledgeCertificateDocumentProps {
  file: AgFinancingFile;
  readonly?: boolean;
}

type PledgeCertificateFileMetadata = Pick<
  AgPhase3FileMetadata,
  | 'pledgeCertificateRegistrationNumber'
  | 'pledgeCertificateRegistrationDate'
  | 'pledgeCertificateLevel'
  | 'pledgeCertificateFirstCompanyRegistered'
  | 'pledgeCertificateSecondCompanyRegistered'
>;

const isPhase3FileCompanyRegisteredValid = (company?: AgPledgeCertificateCompanyRegistered) => {
  return !!(
    company &&
    company.creditor &&
    company.emitter &&
    company.matricula &&
    company.pledgeArea !== null &&
    company.pledgeArea !== undefined &&
    company.volume !== null &&
    company.volume !== undefined
  );
};

const isPhase3FileMetadataValid = (metadata: PledgeCertificateFileMetadata) => {
  return (
    !!(
      metadata.pledgeCertificateRegistrationNumber &&
      metadata.pledgeCertificateRegistrationDate &&
      metadata.pledgeCertificateLevel &&
      (metadata.pledgeCertificateLevel === PledgeLevel.FIRST ||
        (metadata.pledgeCertificateLevel === PledgeLevel.SECOND &&
          isPhase3FileCompanyRegisteredValid(metadata.pledgeCertificateFirstCompanyRegistered)) ||
        (metadata.pledgeCertificateLevel === PledgeLevel.THIRD &&
          isPhase3FileCompanyRegisteredValid(metadata.pledgeCertificateFirstCompanyRegistered) &&
          isPhase3FileCompanyRegisteredValid(metadata.pledgeCertificateSecondCompanyRegistered)))
    ) || isPledgeNegativeValid(metadata)
  );
};

const isPledgeNegativeValid = (metadata: PledgeCertificateFileMetadata) =>
  !!(metadata.pledgeCertificateLevel && metadata.pledgeCertificateLevel === PledgeLevel.NEGATIVE);

export const PledgeCertificateDocument: React.FunctionComponent<PledgeCertificateDocumentProps> = ({
  file,
  readonly = false
}) => {
  /**
   * State
   */
  const [metadata, setMetadata] = useState<PledgeCertificateFileMetadata>({ ...file.metadata?.phase3FileInfo });
  const [isUpdatingInformation, setIsUpdatingInformation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [registrationNumberInputError, setRegistrationNumberInputError] = useState('');
  const [registrationDateInputError, setRegistrationDateInputError] = useState('');
  const [pledgeLevelInputError, setPledgeLevelInputError] = useState('');

  /**
   * Styles
   */
  const classes = useStyles();

  /**
   * Mutations
   */
  const [updateAgFinancingFileFn] = useMutation(updateAgFinancingFile);

  const { addFlashMessage } = useFlashMessage();

  const updateRegisteredCompanyInfo = (
    companyField: 'pledgeCertificateFirstCompanyRegistered' | 'pledgeCertificateSecondCompanyRegistered'
  ) => {
    return (companyInfo: AgPledgeCertificateCompanyRegistered) => {
      setMetadata({
        ...metadata,
        [companyField]: companyInfo
      });
      setIsUpdatingInformation(true);
    };
  };

  const updateRegistrationNumber = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setMetadata({
      ...metadata,
      pledgeCertificateRegistrationNumber: event.target.value
    });
    setIsUpdatingInformation(true);
  };

  const updateRegistrationDate = (date: MaterialUiPickersDate): void => {
    setMetadata({ ...metadata, pledgeCertificateRegistrationDate: date._d });
    setIsUpdatingInformation(true);
  };

  const updatePledgeLevel = (event: SelectChangeEvent<string>): void => {
    setMetadata({ ...metadata, pledgeCertificateLevel: event.target.value as PledgeLevel });
    setIsUpdatingInformation(true);
  };

  usePrompt(isUpdatingInformation);

  /**
   * Runs simple validation checks against several inputs, setting error states if necessary
   *
   * @returns true if all validations pass, false otherwise
   */
  const validateInputs = (): boolean => {
    setRegistrationNumberInputError('');
    setRegistrationDateInputError('');
    setPledgeLevelInputError('');
    const errorMessage = i18next.t('inputFieldRequiredError');
    let validationPassed = true;
    if (!metadata.pledgeCertificateRegistrationNumber) {
      setRegistrationNumberInputError(errorMessage);
      validationPassed = false;
    }
    if (!metadata.pledgeCertificateRegistrationDate) {
      setRegistrationDateInputError(errorMessage);
      validationPassed = false;
    }
    if (!metadata.pledgeCertificateLevel) {
      setPledgeLevelInputError(errorMessage);
      validationPassed = false;
    }
    return validationPassed;
  };

  const updateDocumentInfo = () => {
    if (!validateInputs()) return;

    setIsSaving(true);

    const updatedMetadata = { ...metadata };
    if (updatedMetadata.pledgeCertificateLevel === PledgeLevel.SECOND) {
      delete updatedMetadata.pledgeCertificateSecondCompanyRegistered;
    } else if (updatedMetadata.pledgeCertificateLevel === PledgeLevel.FIRST) {
      delete updatedMetadata.pledgeCertificateFirstCompanyRegistered;
      delete updatedMetadata.pledgeCertificateSecondCompanyRegistered;
    }

    const updatedDocument = [
      {
        id: file.id,
        metadata: {
          ...file.metadata,
          phase3FileInfo: {
            ...updatedMetadata
          }
        }
      }
    ];
    updateAgFinancingFileFn({
      variables: {
        data: updatedDocument
      }
    })
      .then(() => {
        // * Update metadata to immediately update the visualized info (without refreshing)
        setMetadata(updatedMetadata);
        setIsUpdatingInformation(false);

        addFlashMessage({
          content: utils.text.firstLetterUppercase(i18next.t('certificateOfPledgeUpdatedSuccessfully')),
          title: '',
          variant: 'success'
        });
      })
      .catch((e: ApolloError) => {
        addFlashMessage({
          content: e.graphQLErrors[0].message,
          title: '',
          variant: 'error'
        });
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const isSaveDisabled =
    !isUpdatingInformation || !isPhase3FileMetadataValid(metadata) || !isPledgeNegativeValid(metadata);

  return readonly ? (
    <div className={classes.card}>
      <PledgeCertificateDocumentSummary phase3FileInfo={metadata} />
    </div>
  ) : (
    <>
      <GridContainer columns={3}>
        <Input
          label={i18next.t('registrationNumber')}
          name="registrationNumber"
          onChange={updateRegistrationNumber}
          value={metadata.pledgeCertificateRegistrationNumber || ''}
          sizeVariant="slim"
          error={registrationNumberInputError}
        />
        <DatePicker
          name="registrationDate"
          label={i18next.t('registrationDate')}
          value={metadata.pledgeCertificateRegistrationDate || null}
          onChange={updateRegistrationDate}
          sizeVariant="slim"
          error={registrationDateInputError}
        />
        <Select
          label={utils.text.toTitleCase(i18next.t('pledgeLevel'))}
          value={metadata.pledgeCertificateLevel}
          name="stateId"
          options={pledgeLevelOptions}
          onChange={updatePledgeLevel}
          sizeVariant="slim"
          error={pledgeLevelInputError}
        />
      </GridContainer>
      {!!metadata.pledgeCertificateLevel &&
      [PledgeLevel.SECOND, PledgeLevel.THIRD].includes(metadata.pledgeCertificateLevel) ? (
        <PledgeCertificateRegisteredCompany
          title={i18next.t('firstCompanyRegistered')}
          companyInfo={metadata.pledgeCertificateFirstCompanyRegistered}
          onCompanyInfoUpdated={updateRegisteredCompanyInfo('pledgeCertificateFirstCompanyRegistered')}
        />
      ) : null}
      {!!metadata.pledgeCertificateLevel && metadata.pledgeCertificateLevel === PledgeLevel.THIRD ? (
        <PledgeCertificateRegisteredCompany
          title={i18next.t('secondCompanyRegistered')}
          companyInfo={metadata.pledgeCertificateSecondCompanyRegistered}
          onCompanyInfoUpdated={updateRegisteredCompanyInfo('pledgeCertificateSecondCompanyRegistered')}
        />
      ) : null}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={updateDocumentInfo} type="submit" size="small" busy={isSaving} disabled={isSaveDisabled}>
          {i18next.t('save')}
        </Button>
      </div>
    </>
  );
};
